<template>
  <div class="text-left">
    <v-form class="pa-6" ref="form">
      <v-stepper v-model="step">
        <v-stepper-header class="elevation-0">
          <v-stepper-step :complete="step > 1" step="1">{{
            $t("project.basicData")
          }}</v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step step="2" :complete="step > 2">{{
            $t("project.consortiumData")
          }}</v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step step="3" :complete="step > 3">{{
            $t("project.subprojectsData")
          }}</v-stepper-step>

          <v-divider></v-divider>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1">
            <v-row>
              <v-col cols="12" class="md-0">

                <MultilingualTextField v-if="load_complete" v-model="project.title" :label="$t('form.project.title')" entity-type="project" field="title" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <h5>
                  {{ $t("form.project.acronym") }}
                </h5>
                <v-row>
                  <v-col class="py-0">
                    <v-text-field
                      v-model="project.acronym"
                      :label="$t('form.project.acronym')"
                      outlined
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="6" sm="6">
                <h5>
                  {{ $t("type") }}
                </h5>
                <v-row>
                  <v-col class="py-0">
                    <v-select
                      v-model="project.type"
                      :items="projectTypes"
                      item-value="value"
                      item-text="text"
                      :label="$t('type')"
                      outlined
                      :rules="rules.fieldRequired"
                    ></v-select>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <h5>
                  {{ $t('form.doi') }}
                </h5>
                <v-row>
                  <v-col cols="12" class="py-0">
                    <v-text-field
                      v-model="project.doi"
                      :label="$t('form.doi')"
                      required
                      outlined
                      :rules="rules.doiRules"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="6">
                <h5>
                  {{ $t('form.projectcode') }}
                </h5>
                <v-row>
                  <v-col cols="12" class="py-0">
                    <v-text-field
                      v-model="project.code"
                      :label="$t('form.projectcode')"
                      required
                      outlined
                      :rules="rules.projectcodeRules"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <h5 class="py-0">
                  {{ $t("form.project.visibility") }}
                </h5>
                <v-row>
                  <v-col cols="4" class="py-0">
                    <v-checkbox
                      v-model="project.external"
                      :label="$t('form.project.external_more')"
                    />
                  </v-col>
                  <v-col cols="4" class="py-0">
                    <v-checkbox
                      class="big"
                      v-model="project.hidden"
                      :label="$t('form.hidden')"
                    />
                  </v-col>
                  <v-col cols="4" class="py-0">
                    <v-checkbox
                      class="big"
                      v-model="project.featured"
                      :label="$t('form.featured')"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <h5>
              {{ $t("form.project.dates") }}
            </h5>
            <v-row>
              <v-col cols="6" class="py-0">
                <v-menu
                  v-model="menu_start_date"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="project.start_date"
                      :label="$t('form.project.start_date')"
                      prepend-inner-icon="event"
                      readonly
                      outlined
                      v-on="on"
                      :rules="rules.fieldRequired"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="project.start_date"
                    @input="menu_start_date = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="6" class="py-0">
                <v-menu
                  v-model="menu_end_date"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="project.end_date"
                      :label="$t('form.project.end_date')"
                      prepend-inner-icon="event"
                      readonly
                      outlined
                      v-on="on"
                      :rules="rules.fieldRequired"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="project.end_date"
                    :min="project.start_date"
                    @input="menu_end_date = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="py-0 mb-5">
                <MultilingualTextField v-if="load_complete" v-model="project.description" :label="$t('form.description')" entity-type="project" field="description" rich />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="py-0 mb-5">
                <MultilingualTextField v-if="load_complete" v-model="project.objectives" :label="$t('form.project.objectives')" entity-type="project" field="objectives" rich />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="py-0 mb-5">
                <MultilingualTextField v-if="load_complete" v-model="project.results" :label="$t('form.project.results')" entity-type="project" field="results" rich />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <h5>{{ $t("form.project.main_citius_investigators") }}</h5>
                <v-autocomplete
                  v-model="project.main_citius_investigators"
                  :items="namePersons"
                  return-object
                  item-value="id"
                  item-text="name"
                  outlined
                  multiple
                  :label="$t('form.project.main_citius_investigators')"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="6">
                <h5>{{ $t("form.project.citius_investigators") }}</h5>
                <v-autocomplete
                  v-model="project.citius_investigators"
                  :items="namePersons"
                  return-object
                  item-value="id"
                  item-text="name"
                  outlined
                  multiple
                  :label="$t('form.project.citius_investigators')"
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <h5>{{ $t("form.project.main_external_investigators") }}</h5>
                <v-row>
                  <v-col>
                    <ArrayFieldForm
                      v-model="project.main_external_investigators"
                      name-field="main_external_investigators"
                      :label="$t('form.project.main_external_investigators')"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" md="6">
                <h5>{{ $t("form.project.external_investigators") }}</h5>
                <v-row>
                  <v-col>
                    <ArrayFieldForm
                      v-model="project.external_investigators"
                      name-field="external_investigators"
                      :label="$t('form.project.external_investigators')"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <h5 v-if="$route.params.id > -1">
              {{ $t("form.files") }}
            </h5>
            <UploadFiles
              v-if="project.id !== undefined && $route.params.id > -1"
              object_type="project"
              :id="this.project.id"
              :final="true"
            />
            <v-row>
              <v-col>
                <h5>
                  {{ $t("form.project.funder_programs") }}
                </h5>
                <v-row>
                  <v-col>
                    <v-autocomplete
                      v-model="project.funder_programs"
                      :items="funderPrograms"
                      return-object
                      item-value="id"
                      item-text="name"
                      outlined
                      multiple
                      :label="$t('form.project.funder_programs')"
                    ></v-autocomplete>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="py-0 mb-5">
                <MultilingualTextField v-if="load_complete" v-model="project.funder_text" :label="$t('form.funder_text')" entity-type="project" field="funder_text" rich />
              </v-col>
            </v-row>
            <ProjectBudgetFields :total-budget.sync="project.total_budget" :citius-budget.sync="project.citius_budget" />
          <v-row>
            <v-col>
              <h5>
                {{ $t("form.project.additional_text") }}
              </h5>
              <v-text-field
                v-model="project.additional_text"
                :label="$t('form.project.additional_text')"
                outlined
              ></v-text-field>
            </v-col>
          </v-row>
            <v-row>
              <v-col>
                <h5>
                  {{ $t("form.project.contractor") }}
                </h5>
                <v-autocomplete
                  v-model="project.contractor"
                  :label="$t('form.project.contractor')"
                  :items="organizations"
                  item-value="id"
                  item-text="name"
                  return-object
                  outlined
                ></v-autocomplete>
              </v-col>
            </v-row>
            <h5>
              {{ $t("form.project.image") }}
            </h5>
            <UploadImage
              :path="$store.state.endpoint + '/project/' + project.id"
              :url="$store.state.endpoint + project.image"
              :object="{ name: project.image }"
              :object_created="project.id !== undefined"
              name_field="image"
            />
            <h5>
              {{ $t("form.project.image_squared") }}
            </h5>
            <UploadImage
              :path="$store.state.endpoint + '/project/' + project.id"
              :url="$store.state.endpoint + project.image_squared"
              :object="{ name: project.image_squared }"
              :object_created="project.id !== undefined"
              name_field="image_squared"
            />
            <v-row>
              <v-col>
                <h5>
                  {{ $t("form.project.uri") }}
                </h5>
                <v-row>
                  <v-col class="py-0">
                    <v-text-field
                      v-model="project.uri"
                      :label="$t('form.project.uri')"
                      outlined
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <h5>
                  {{ $t("form.project.video_id") }}
                </h5>
                <v-row>
                  <v-col class="py-0">
                    <v-text-field
                      v-model="project.video_id"
                      :label="$t('form.project.video_id')"
                      :rules="rules.youtubeIDRules"
                      outlined
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <h5>
              {{ $t("form.publication.research_areas") }}
            </h5>
            <v-row>
              <v-col cols="12" class="py-0">
                <v-select
                  :items="researchAreas"
                  v-model="project.research_areas"
                  :label="$t('form.publication.research_areas')"
                  required
                  :item-text="'name.' + $i18n.locale"
                  item-value="id"
                  multiple
                  :rules="rules.fieldRequired"
                  outlined
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6" class="text-left my-6">
                <v-btn class="mr-4" v-if="step > 1" @click="step = step - 1">
                  {{ $t("button.previous") }}
                </v-btn>
              </v-col>
              <v-col cols="6" class="text-right my-6">
                <v-btn class="mr-4" v-if="step < 3" @click="step = step + 1">
                  {{ $t("button.next") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-stepper-content>

          <v-stepper-content step="2">
            <v-row>
              <v-col>
                <v-checkbox
                  v-model="has_consortium"
                  :label="$t('form.project.has_consortium')"
                />
              </v-col>
            </v-row>
            <div v-if="has_consortium">
              <h5>
                {{ $t("form.project.consortium_dates") }}
              </h5>
              <v-row>
                <v-col cols="6" class="py-0">
                  <v-menu
                    v-model="menu_consortium_start_date"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="project.consortium.start_date"
                        :label="$t('form.project.start_date')"
                        prepend-inner-icon="event"
                        readonly
                        outlined
                        v-on="on"
                        :rules="rules.fieldRequired"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="project.consortium.start_date"
                      @input="menu_consortium_start_date = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="6" class="py-0">
                  <v-menu
                    v-model="menu_consortium_end_date"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="project.consortium.end_date"
                        :label="$t('form.project.end_date')"
                        prepend-inner-icon="event"
                        readonly
                        outlined
                        v-on="on"
                        :rules="rules.fieldRequired"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="project.consortium.end_date"
                      :min="project.consortium.start_date"
                      @input="menu_consortium_end_date = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="py-0">
                  <v-checkbox
                    indeterminate
                    v-model="project.consortium.is_citius"
                    :label="$t('form.project.consortium_citius')"
                    :rules="rules.checkboxRequired"
                  />
                </v-col>
              </v-row>
              <h5>
                {{ $t("form.project.leader") }}
              </h5>
              <v-row>
                <v-col class="py-0">
                  <v-autocomplete
                    v-model="project.consortium.leader"
                    :items="organizations"
                    return-object
                    item-text="name"
                    outlined
                    :label="$t('form.project.leader')"
                    :rules="rules.fieldRequired"
                  ></v-autocomplete>
                </v-col>
              </v-row>
              <h5>
                {{ $t("form.project.partners") }}
              </h5>
              <v-row>
                <v-col class="py-0">
                  <v-autocomplete
                    v-model="project.consortium.partners"
                    :items="organizations"
                    return-object
                    item-text="name"
                    outlined
                    :label="$t('form.project.partners')"
                    multiple
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </div>
            <v-row>
              <v-col cols="6" class="text-left my-6">
                <v-btn class="mr-4" v-if="step > 1" @click="step = step - 1">
                  {{ $t("button.previous") }}
                </v-btn>
              </v-col>
              <v-col cols="6" class="text-right my-6">
                <v-btn class="mr-4" v-if="step < 3" @click="step = step + 1">
                  {{ $t("button.next") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-stepper-content>

          <v-stepper-content step="3">
            <v-row>
              <v-col>
                <v-btn
                  rounded
                  color="primary"
                  class="ma-2 white--text"
                  @click="addSubproject"
                >
                  <v-icon dark>
                    mdi-plus
                  </v-icon>
                  {{ $t("button.addSubproject") }}
                </v-btn>
              </v-col>
            </v-row>
            <div
              v-for="(subproject, index) in project.subprojects"
              :key="index"
            >
              <h4 class="text-left mb-5">
                {{ $t("form.project.subproject") }} {{ index + 1 }}
              </h4>

              <v-row>
                <v-col cols="12" class="py-0">
                  <MultilingualTextField v-if="load_complete" v-model="subproject.title" :label="$t('form.project.title')" entity-type="project" field="subproject_title" />
                </v-col>
              </v-row>
              <h5>
                {{ $t("form.project.dates") }}
              </h5>
              <v-row>
                <v-col cols="6" class="py-0">
                  <v-menu
                    v-model="tabs_subprojects[index].menu_start_date"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="subproject.start_date"
                        :label="$t('form.project.start_date')"
                        prepend-inner-icon="event"
                        readonly
                        outlined
                        v-on="on"
                        :rules="rules.fieldRequired"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="subproject.start_date"
                      @input="tabs_subprojects[index].menu_start_date = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="6" class="py-0">
                  <v-menu
                    v-model="tabs_subprojects[index].menu_end_date"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="subproject.end_date"
                        :label="$t('form.project.end_date')"
                        prepend-inner-icon="event"
                        readonly
                        outlined
                        v-on="on"
                        :rules="rules.fieldRequired"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="subproject.end_date"
                      :min="subproject.start_date"
                      @input="tabs_subprojects[index].menu_end_date = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>


              <v-row>
                <v-col cols="12" class="py-0">
                  <MultilingualTextField v-if="load_complete" v-model="subproject.description" :label="$t('form.description')" entity-type="project" field="subproject_description" rich />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <h5>{{ $t("form.project.main_citius_investigators") }}</h5>
                  <v-autocomplete
                    v-model="subproject.main_citius_investigators"
                    :items="namePersons"
                    return-object
                    item-value="id"
                    item-text="name"
                    outlined
                    multiple
                    :label="$t('form.project.main_citius_investigators')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="6">
                  <h5>{{ $t("form.project.citius_investigators") }}</h5>
                  <v-autocomplete
                    v-model="subproject.citius_investigators"
                    :items="namePersons"
                    return-object
                    item-value="id"
                    item-text="name"
                    outlined
                    multiple
                    :label="$t('form.project.citius_investigators')"
                  ></v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <h5>
                    {{ $t("form.project.main_external_investigators") }}
                  </h5>
                  <v-row>
                    <v-col>
                      <ArrayFieldForm
                        v-model="subproject.main_external_investigators"
                        name-field="main_external_investigators"
                        :label="$t('form.project.main_external_investigators')"
                      />
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="12" md="6">
                  <h5>{{ $t("form.project.external_investigators") }}</h5>
                  <v-row>
                    <v-col>
                      <ArrayFieldForm
                        v-model="subproject.external_investigators"
                        name-field="external_investigators"
                        :label="$t('form.project.external_investigators')"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <h5>
                    {{ $t("form.project.contractor") }}
                  </h5>
                  <v-autocomplete
                    v-model="subproject.contractor"
                    :label="$t('form.project.contractor')"
                    :items="organizations"
                    item-value="id"
                    item-text="name"
                    return-object
                    outlined
                  ></v-autocomplete>
                </v-col>
              </v-row>
              <v-divider />
            </div>
            <v-row>
              <v-col class="text-right py-0">
                <v-btn
                  text
                  v-if="project.subprojects.length"
                  color="red"
                  class="ma-2"
                  @click="deleteSubproject"
                >
                  {{ $t("button.deleteSubproject") }}
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6" class="text-left my-6">
                <v-btn class="mr-4" v-if="step > 1" @click="step = step - 1">
                  {{ $t("button.previous") }}
                </v-btn>
              </v-col>
              <v-col cols="6" class="text-right my-6">
                <v-btn class="mr-4" v-if="step < 3" @click="step = step + 1">
                  {{ $t("button.next") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
      <v-divider />
      <div class="text-center">
        <v-btn text @click="$router.back()"><v-icon left>mdi-arrow-left</v-icon>{{ $t("button.back") }}</v-btn>
        <v-btn color="primary" @click="save" class="ml-2"><v-icon left>mdi-content-save</v-icon>{{ $t("button.save") }}</v-btn>
      </div>
    </v-form>
  </div>
</template>

<script>
import axios from "axios";
import validationRules from "../../mixins/validationRules";
import project_model from "../../mixins/model_utils/project_model";
import project_data from "../../mixins/project_data";
import ArrayFieldForm from "../../components/reusables/ArrayFieldForm";
import UploadImage from "../../components/layouts/UploadImage";
import UploadFiles from "../../components/reusables/UploadFiles";
import MultilingualTextField from "@/components/reusables/MultilingualTextField.vue";
import ProjectBudgetFields from "@/components/projects/ProjectBudgetFields.vue";

export default {
  components: {
    UploadFiles,
    UploadImage,
    ArrayFieldForm,
    MultilingualTextField,
    ProjectBudgetFields
  },
  mixins: [validationRules, project_model, project_data],
  name: "EditProjectDetail",
  data: () => ({
    step: 1,
    tab: null,
    tab_title: 0,
    tab_description: 0,
    tab_objectives: 0,
    tab_results: 0,
    tab_fundertext: 0,
    menu_start_date: false,
    menu_end_date: false,
    menu_consortium_start_date: false,
    menu_consortium_end_date: false
  }),

  created() {
    this.getOrganizations();
    this.getAllResearchAreas();
    this.getFunderPrograms();
    this.getNamePersons();
    this.getProjectTypes();
    if (this.$route.params.id > -1) {
      this.getProjectToUpdate(
        this.$store.state.endpoint + "/project/" + this.$route.params.id
      );
    }
    if(this.$route.params.id === undefined){
      this.load_complete = true;
    }
  },

  computed: {
    processData() {
      let title = "";
      if (
        this.project.title["gl"] !== "" &&
        this.project.title["gl"] !== undefined
      ) {
        title = this.project.title["gl"];
      } else if (
        this.project.title["es"] !== "" &&
        this.project.title["es"] !== undefined
      ) {
        title = this.project.title["es"];
      } else if (
        this.project.title["en"] !== "" &&
        this.project.title["en"] !== undefined
      ) {
        title = this.project.title["en"];
      }
      let data = {
        title: this.project.title,
        description: this.project.description,
        start_date: this.project.start_date,
        end_date: this.project.end_date,
        objectives: this.project.objectives,
        results: this.project.results,
        acronym: this.project.acronym,
        doi: this.project.doi,
        code: this.project.code,
        type: this.project.type,
        external: this.project.external,
        hidden: this.project.hidden,
        featured: this.project.featured,
        main_citius_investigators: this.project.main_citius_investigators.map(
          function(item) {
            return item.id;
          }
        ),
        citius_investigators: this.project.citius_investigators.map(function(
          item
        ) {
          return item.id;
        }),
        main_external_investigators: this.project.main_external_investigators,
        external_investigators: this.project.external_investigators,
        funder_programs: this.project.funder_programs.map(function(item) {
            return item.id;
          }),
        funder_text: this.project.funder_text,
        total_budget: this.project.total_budget === '' ? null : this.project.total_budget,
        citius_budget: this.project.citius_budget === '' ? null : this.project.citius_budget,
        additional_text: this.project.additional_text,
        subprojects: this.project.subprojects,
        uri: this.project.uri,
        video_id: this.project.video_id,
        research_areas: this.project.research_areas.map(a => typeof a == "object" ? a.id : a).filter(area =>
          Number.isInteger(area)
        )
      };

      data["subprojects"].forEach(subproject => {
        subproject["main_citius_investigators"] = subproject[
          "main_citius_investigators"
        ]
          .filter(item => item.id != null)
          .map(function(item) {
            return item.id;
          });
        subproject["citius_investigators"] = subproject["citius_investigators"].map(function(item) {
            return item.id;
          });
        subproject["contractor"] = subproject["contractor"] != null ? subproject.contractor.id : null;
      });

      if (this.has_consortium) {
        data["consortium"] = this.project.consortium;
        data["consortium"]["leader"] = this.project.consortium.leader.id;
        data["consortium"]["partners"] = this.project.consortium.partners.map(
          partner => {
            return partner.id;
          }
        );
      }

      data["contractor"] = (this.project.contractor != null) ? this.project.contractor.id : null;

      return data;
    }
  },

  methods: {
    addSubproject() {
      this.tabs_subprojects.push({
        tab_title: 0,
        tab_description: 0,
        menu_start_date: false,
        menu_end_date: false
      });
      this.project.subprojects.push(
        JSON.parse(JSON.stringify(this.subproject))
      );
    },

    deleteSubproject() {
      this.tabs_subprojects.pop();
      this.project.subprojects.pop();
    },
    save() {
      if (this.$refs.form.validate()) {
        let path;
        if (this.$route.params.id > -1) {
          path =
            this.$store.state.endpoint + "/project/" + this.$route.params.id;
          axios
            .patch(path, this.processData)
            .then(() => {
              this.$store.commit("alert", {
                color: "success",
                text: this.$t("message.correct.updateItem")
              });
              this.$router.push("/manage/edit/projects");
            })
            .catch(error => {
              this.$store.commit("alert", {
                color: "error",
                text: error.response.data
              });
            });
        } else {
          path = this.$store.state.endpoint + "/project";
          axios
            .post(path, this.processData)
            .then(response => {
              this.$store.commit("alert", {
                color: "success",
                text: this.$t("message.correct.createItem")
              });
              this.$router.push("/manage/edit/projects");
            })
            .catch(error => {
              console.log(error);
              this.$store.commit("alert", {
                color: "error",
                text: error.response.data
              });
            });
        }
      }
    },
    changeContent(lan, content, field) {
      this.project[field][lan] = content;
    },
    changeSubprojectContent(lan, content, field, index) {
      this.project.subprojects[index][field][lan] = content;
    }
  }
};
</script>
